import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import EmailIllustrationSrc from "images/email-illustration.svg";

const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const TextColumn = styled(Column)(props => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`
]);

const Image = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`rounded bg-contain bg-no-repeat bg-center h-full`,
]);
const TextContent = tw.div`lg:py-8 text-center md:text-left`;

const Subheading = tw(SubheadingBase)`text-center md:text-left text-black`;
const Heading = tw(SectionHeading)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const Form = tw.form`mt-8 md:mt-10 text-sm flex flex-col max-w-sm mx-auto md:mx-0`;
const Input = tw.input`mt-6 first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-black`;
const Textarea = styled(Input).attrs({ as: "textarea" })`
  ${tw`h-24`}
`;

const SubmitButton = tw(PrimaryButtonBase)`inline-block mt-8`;
const SuccessMessage = tw.p`text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100 mt-8`;

export default ({
  subheading = "",
  heading = "",
  description = "",
  submitButtonText = "",
  formAction = "/#",
  formMethod = "POST",
  textOnLeft = true,
}) => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(data).toString(),
    })
      .then(() => setSubmitted(true))
      .catch((error) => alert(error));
  };

  return (
    <Container>
      <TwoColumn>
        <ImageColumn>
          <Image imageSrc={EmailIllustrationSrc} />
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            {subheading && <Subheading>{t('contactUsForm.subheading')}</Subheading>}
            <Heading>
              {t('contactUsForm.headingPart1')} <span tw="text-black">{t('contactUsForm.headingHighlighted')}</span>{t('contactUsForm.headingPart2')}
            </Heading>
            {description && <Description>{t('contactUsForm.description')}</Description>}
            {!submitted ? (
              <Form
                name="contact"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
              >
                <input type="hidden" name="form-name" value="contact" />
                <p hidden>
                  <label>Don’t fill this out: <input name="bot-field" /></label>
                </p>
                <Input type="email" name="email" placeholder={t('contactUsForm.formFields.emailPlaceholder')} required />
                <Input type="text" name="name" placeholder={t('contactUsForm.formFields.namePlaceholder')} required />
                <Input type="text" name="subject" placeholder={t('contactUsForm.formFields.subjectPlaceholder')} required />
                <Textarea name="message" placeholder={t('contactUsForm.formFields.messagePlaceholder')} required />
                <SubmitButton type="submit">{t('contactUsForm.submitButtonText')}</SubmitButton>
              </Form>
            ) : (
              <SuccessMessage>
                {t('contactUsForm.successMessage')}
              </SuccessMessage>
            )}
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};
